/**
 * Customer Groups Management Page
 * 
 * Dedicated page for managing customer groups with CRUD operations.
 * Features:
 * - List all customer groups
 * - Create new groups
 * - Edit existing groups
 * - Delete groups
 * - View group details and assigned customers
 * 
 * Requirements: Customer Management System
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  TextField,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import {
  useGetCustomerGroupsQuery,
  useDeleteCustomerGroupMutation,
} from '../store/services/customerApi';
import PageHeader from '../components/ui/PageHeader';

const CustomerGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<any>(null);

  // API queries
  const {
    data: groupsData,
    isLoading,
    refetch,
  } = useGetCustomerGroupsQuery({
    page: page + 1,
    per_page: pageSize,
    search: search || undefined,
  });

  // Mutations
  const [deleteGroup] = useDeleteCustomerGroupMutation();

  // Handlers
  const handleAddGroup = () => {
    navigate('/admin/parties/customer-groups/new');
  };

  const handleEditGroup = (id: string) => {
    navigate(`/admin/parties/customer-groups/edit/${id}`);
  };

  const handleDeleteClick = (group: any) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    try {
      await deleteGroup(groupToDelete.id).unwrap();
      enqueueSnackbar('Customer group deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      refetch();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.detail || 'Failed to delete customer group',
        { variant: 'error' }
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  // Columns
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Group Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'default_credit_limit',
      headerName: 'Credit Limit',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          ₹{params.value?.toLocaleString('en-IN') || '0'}
        </Typography>
      ),
    },
    {
      field: 'default_credit_days',
      headerName: 'Credit Days',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0} days
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          sx={{ borderRadius: 4 }}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit Group">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleEditGroup(params.row.id)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete Group">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
          showInMenu={false}
        />,
      ],
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Customer Groups"
        subtitle="Manage customer groups and their business rules"
        actions={[
          {
            label: 'Refresh',
            onClick: refetch,
            variant: 'outlined',
            icon: <RefreshIcon />,
          },
          {
            label: 'Add Customer Group',
            onClick: handleAddGroup,
            variant: 'contained',
            color: 'secondary',
            icon: <AddIcon />,
          },
        ]}
      />

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search customer groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* Data Grid */}
        <DataGrid
          rows={groupsData?.items || []}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          rowCount={groupsData?.total || 0}
          paginationMode="server"
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#0d47a1',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.04)',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Customer Group
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the customer group "{groupToDelete?.name}"?
            This action cannot be undone. Customers in this group will need to be reassigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerGroupsPage;
